/**
 * GridCellsProvider
 *
 * Singleton that subscribes to pre-aggregated territory grid cells from Firestore.
 * Mirrors TilesProvider's patterns (singleton, geohash-band subscriptions, debounce).
 *
 * Zoom routing:
 *   zoom >= 15: no-op (individual tiles handle rendering)
 *   zoom 8–14: subscribe to ptw-cells-fine  (0.01°, ~1.1km)
 *   zoom < 8:  subscribe to ptw-cells-coarse (0.1°, ~11km)
 */

import { Firestore, getFirestore } from 'firebase/firestore';
import { geohashQueryBounds } from 'geofire-common';
import * as turf from '@turf/turf';
import firebaseBackend from '../../../../backend/firebase';
import { COLLECTIONS } from '@painttheworld/shared';
import { MapState } from '../../map/slice';
import { GridCell, setFineCells, setCoarseCells, clearCells, removeCell, setCellsLoading } from './slice';
import { GeohashSubscriptionManager } from '../helpers/GeohashSubscriptionManager';

// Lazy store access to break circular dependency
const getStore = (): any => require('../../../store').store;

/** At zoom >= 15, individual tiles take over — no cells needed */
const ZOOM_CELLS_CUTOFF = 15;
/** At zoom < 8, switch from fine to coarse cells */
const ZOOM_FINE_COARSE_BOUNDARY = 8;

/** Maximum radius (meters) for fine cell queries (zoom 8–14) */
const MAX_RADIUS_FINE_M = 50_000;   // 50 km
/** Maximum radius (meters) for coarse cell queries (zoom < 8) */
const MAX_RADIUS_COARSE_M = 300_000; // 300 km

export default class GridCellsProvider {
    private static _instance: GridCellsProvider;

    public static get instance(): GridCellsProvider {
        if (!GridCellsProvider._instance) {
            GridCellsProvider._instance = new GridCellsProvider();
        }
        return GridCellsProvider._instance;
    }

    private constructor() {}

    private _firestore?: Firestore;
    private get firestore(): Firestore {
        if (!this._firestore) {
            this._firestore = getFirestore(firebaseBackend);
        }
        return this._firestore;
    }

    /** Current resolution being subscribed to (null = not subscribed) */
    private _activeResolution: 'fine' | 'coarse' | null = null;
    private _manager = new GeohashSubscriptionManager();

    /**
     * Fetch (subscribe to) grid cells for the given map state.
     * Called from TerritoryLayer on viewport change, debounced by the layer.
     *
     * @param mapState  - Current Redux map state (location + zoom)
     * @param mapBoundsGetter - Callable that returns the live Mapbox map bounds
     */
    getCells(mapState: MapState, mapBoundsGetter: () => mapboxgl.LngLatBounds): void {
        const zoom = mapState.locationAndZoom.zoom;

        // Above zoom 15: individual tiles take over — disconnect cells
        if (zoom >= ZOOM_CELLS_CUTOFF) {
            if (this._activeResolution !== null) {
                this.disconnect();
            }
            return;
        }

        const targetResolution: 'fine' | 'coarse' = zoom >= ZOOM_FINE_COARSE_BOUNDARY ? 'fine' : 'coarse';
        const collectionName = targetResolution === 'fine' ? COLLECTIONS.GRID_CELLS_FINE : COLLECTIONS.GRID_CELLS_COARSE;
        const maxRadius = targetResolution === 'fine' ? MAX_RADIUS_FINE_M : MAX_RADIUS_COARSE_M;

        // If resolution changed, disconnect old subscriptions
        if (this._activeResolution !== null && this._activeResolution !== targetResolution) {
            this._manager.disconnect();
            getStore().dispatch(clearCells());
            this._activeResolution = null;
        }

        const center = mapState.locationAndZoom.location;

        // Derive radius from live Mapbox bounds
        const liveBounds = mapBoundsGetter();
        const radiusToCorner = turf.distance(
            [center.longitude, center.latitude],
            [liveBounds.getEast(), liveBounds.getNorth()],
            { units: 'meters' },
        );
        const radiusInMeters = Math.min(radiusToCorner, maxRadius);

        // Disconnect previous subscriptions and re-subscribe with new bounds
        this._manager.disconnect();
        this._activeResolution = targetResolution;

        getStore().dispatch(setCellsLoading(true));

        const bounds = geohashQueryBounds([center.latitude, center.longitude], radiusInMeters);

        let delivered = 0;
        this._manager.subscribe(
            bounds,
            this.firestore,
            collectionName,
            (snapshot) => {
                this._handleSnapshot(snapshot, targetResolution);
                delivered++;
                if (delivered === bounds.length) {
                    getStore().dispatch(setCellsLoading(false));
                }
            },
        );
    }

    private _handleSnapshot(snapshot: any, resolution: 'fine' | 'coarse'): void {
        const cells: GridCell[] = [];
        const removedCellIds: string[] = [];

        snapshot.docChanges().forEach((change: any) => {
            switch (change.type) {
                case 'added':
                case 'modified': {
                    const data = change.doc.data();
                    cells.push({
                        cellId: data.cellId,
                        geohash: data.geohash,
                        dominantColor: data.dominantColor,
                        colorCounts: data.colorCounts,
                    });
                    break;
                }
                case 'removed':
                    // Cell document was deleted by CF (last tile in region removed)
                    removedCellIds.push(change.doc.id);
                    break;
            }
        });

        const store = getStore();

        if (cells.length > 0) {
            if (resolution === 'fine') {
                store.dispatch(setFineCells(cells));
            } else {
                store.dispatch(setCoarseCells(cells));
            }
        }

        for (const cellId of removedCellIds) {
            store.dispatch(removeCell({ resolution, cellId }));
        }
    }

    /** Unsubscribe all listeners and reset state */
    disconnect(): void {
        this._manager.disconnect();
        this._activeResolution = null;
        getStore().dispatch(clearCells());
        getStore().dispatch(setCellsLoading(false));
    }
}
